import React, { useState } from 'react';
import { Collapse } from 'react-bootstrap';
import { ChevronDown, ChevronRight, CircleCheck, KeyRound } from 'lucide-react';
import { Unit } from '../../types/units';
import { UnitKind, UnitTenure } from '../../types/Enums';
import { translate } from '../../types/EnumTranslations';
import { LevelModel, RoomModel } from '../../types/protocols';
import { isUnitLeasable, isUnitRented, unitTypeIcon } from '../../Helpers/unitUtils';
import { levelDisplayName } from '../../Helpers/levelDisplayName';
import { UnitSection, unitLevelPanels } from './unitsView';

interface UnitListProps {
    /** Grouped by trappuppgång (`unitSections`); one untitled group when the building has none. */
    sections: UnitSection[];
    selectedUnitKey: string | null;
    levels: { [levelKey: string]: LevelModel };
    /** The building's rooms; a unit's levels and placement come from them (plan D5). */
    rooms: { [roomKey: string]: RoomModel };
    onSelect: (unitKey: string) => void;
}

const formatArea = (area: number): string => `${area.toLocaleString('sv-SE', { maximumFractionDigits: 1 })} m²`;

const unitMeta = (unit: Unit, levelNames: string[]): string => {
    const parts: string[] = [...levelNames];
    parts.push(translate(unit.tenure));
    if (unit.kind === UnitKind.Apartment && unit.roomCount) parts.push(`${unit.roomCount} rok`);
    if (unit.kind === UnitKind.Commercial && unit.commercialType) parts.push(unit.commercialType);
    if (unit.area) parts.push(formatArea(unit.area));
    return parts.join(' · ');
};

/**
 * Every unit of the building, grouped by trappuppgång with collapsible
 * headers (as Komponenttyper groups by base type). A row shows the unit's
 * levels, flags a unit without rooms or with none placed on a drawing, and
 * ends in its lease badge.
 */
const UnitList: React.FC<UnitListProps> = ({ sections, selectedUnitKey, levels, rooms, onSelect }) => {
    const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(new Set());
    const toggle = (key: string) => setCollapsed(prev => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        return next;
    });

    if (sections.length === 0) {
        return <p className="units-list-empty">Inga lägenheter eller lokaler.</p>;
    }

    const renderRow = (unit: Unit) => {
        const panels = unitLevelPanels(unit, levels, rooms);
        const roomless = panels.length === 0;
        // Placed when one of its rooms has a marker on its level's current drawing.
        const unplaced = !roomless
            && panels.some(panel => panel.blueprintKey)
            && !panels.some(panel => panel.blueprintKey && panel.rooms.some(room => room.blueprintLocations?.[panel.blueprintKey!]));
        const classes = ['units-unit-row'];
        if (selectedUnitKey === unit.key) classes.push('units-unit-row--selected');
        return (
            <li key={unit.key}>
                <button
                    type="button"
                    className={classes.join(' ')}
                    onClick={() => onSelect(unit.key)}
                    aria-pressed={selectedUnitKey === unit.key}
                    title={unit.kind === UnitKind.Commercial ? 'Lokal' : 'Lägenhet'}
                >
                    {/* Type icon as on the drawing; the status is the badge at the end */}
                    <img className="units-unit-icon" src={unitTypeIcon(unit.kind)} alt="" aria-hidden="true" />
                    <span className="units-unit-number">{unit.number || '?'}</span>
                    <span className="units-unit-meta">{unitMeta(unit, panels.map(panel => levelDisplayName(panel.level)))}</span>
                    {roomless && <span className="units-badge units-badge--warning">Utan rum</span>}
                    {unplaced && <span className="units-badge units-badge--warning">Ej placerad</span>}
                    {isUnitLeasable(unit)
                        ? (isUnitRented(unit)
                            ? <span className="units-badge units-badge--muted"><KeyRound size={12} aria-hidden="true" />Uthyrd</span>
                            : <span className="units-badge units-badge--success"><CircleCheck size={12} aria-hidden="true" />Ledig</span>)
                        : unit.tenure === UnitTenure.Own
                            ? <span className="units-badge">Egen</span>
                            : null}
                </button>
            </li>
        );
    };

    return (
        <div className="units-unit-sections">
            {sections.map(section => {
                if (section.title === null) {
                    return <ul key={section.key} className="units-unit-list">{section.units.map(renderRow)}</ul>;
                }
                const open = !collapsed.has(section.key);
                return (
                    <div key={section.key} className="units-unit-section">
                        <button
                            type="button"
                            className="units-unit-section-header"
                            onClick={() => toggle(section.key)}
                            aria-expanded={open}
                        >
                            {open ? <ChevronDown size={16} aria-hidden="true" /> : <ChevronRight size={16} aria-hidden="true" />}
                            <span className="units-unit-section-name">
                                {section.key === 'none' ? section.title : `Trappuppgång ${section.title}`}
                            </span>
                            <span className="badge bg-secondary">{section.units.length}</span>
                        </button>
                        <Collapse in={open}>
                            <div>
                                <ul className="units-unit-list">{section.units.map(renderRow)}</ul>
                            </div>
                        </Collapse>
                    </div>
                );
            })}
        </div>
    );
};

export default UnitList;
